import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Cookie, Settings2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  OPEN_COOKIE_PREFERENCES_EVENT,
  onCookieConsentChange,
  readConsentPreferences,
  saveConsentPreferences,
  shouldShowCookieBannerOnRoute,
  type CookieConsentPreferences,
} from "@/lib/cookieConsent";
import { buildPublicUrl } from "@/lib/adminOrigin";

export const CookieConsentBanner = () => {
  const location = useLocation();
  const [consent, setConsent] = useState<CookieConsentPreferences | null>(() =>
    readConsentPreferences(),
  );
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [functionalEnabled, setFunctionalEnabled] = useState<boolean>(
    () => consent?.functional ?? false,
  );
  const [analyticsEnabled, setAnalyticsEnabled] = useState<boolean>(
    () => consent?.analytics ?? false,
  );

  useEffect(() => {
    const syncFromConsent = (next: CookieConsentPreferences | null) => {
      setConsent(next);
      setFunctionalEnabled(next?.functional ?? false);
      setAnalyticsEnabled(next?.analytics ?? false);
    };

    syncFromConsent(readConsentPreferences());

    const unsubscribe = onCookieConsentChange((updated) => {
      syncFromConsent(updated);
    });

    const handleOpenPreferences = () => {
      const current = readConsentPreferences();
      setFunctionalEnabled(current?.functional ?? false);
      setAnalyticsEnabled(current?.analytics ?? false);
      setPreferencesOpen(true);
    };

    window.addEventListener(
      OPEN_COOKIE_PREFERENCES_EVENT,
      handleOpenPreferences,
    );

    return () => {
      unsubscribe();
      window.removeEventListener(
        OPEN_COOKIE_PREFERENCES_EVENT,
        handleOpenPreferences,
      );
    };
  }, []);

  const handleAcceptAll = () => {
    const saved = saveConsentPreferences({
      functional: true,
      analytics: true,
    });
    setConsent(saved);
    setPreferencesOpen(false);
  };

  const handleNecessaryOnly = () => {
    const saved = saveConsentPreferences({
      functional: false,
      analytics: false,
    });
    setConsent(saved);
    setPreferencesOpen(false);
  };

  const handleSaveCustomPreferences = () => {
    const saved = saveConsentPreferences({
      functional: functionalEnabled,
      analytics: analyticsEnabled,
    });
    setConsent(saved);
    setPreferencesOpen(false);
  };

  const handleOpenModal = () => {
    const current = readConsentPreferences();
    setFunctionalEnabled(current?.functional ?? false);
    setAnalyticsEnabled(current?.analytics ?? false);
    setPreferencesOpen(true);
  };

  const isEligibleRoute =
    typeof window !== "undefined" &&
    shouldShowCookieBannerOnRoute({
      pathname: location.pathname,
      hostname: window.location.hostname,
      search: location.search,
      isDev: Boolean(import.meta.env.DEV),
    });

  const showFloatingBanner = consent === null && isEligibleRoute && !preferencesOpen;

  const isAdminHost =
    typeof window !== "undefined" &&
    (window.location.hostname.startsWith("admin.") ||
      (import.meta.env.DEV &&
        new URLSearchParams(location.search).get("subdomain") === "admin"));

  const privacyPolicyHref =
    typeof window !== "undefined" && isAdminHost
      ? buildPublicUrl(window.location, "/privacy-policy")
      : "/privacy-policy";

  return (
    <>
      {showFloatingBanner && (
        <aside
          role="region"
          aria-label="Cookie consent"
          data-testid="cookie-consent-banner"
          className="fixed bottom-4 right-4 z-40 w-[calc(100vw-2rem)] sm:w-[400px] rounded-xl border border-border bg-card p-4 text-card-foreground shadow-lg"
        >
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Cookie className="h-4 w-4" aria-hidden="true" />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-sm font-semibold text-foreground">
                Cookie Preferences
              </h2>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                We use strictly necessary storage to keep Dibs secure and
                running, plus optional cookies for UI preferences and error
                diagnostics. Read our{" "}
                {isAdminHost ? (
                  <a
                    href={privacyPolicyHref}
                    className="font-medium text-primary hover:underline"
                  >
                    Privacy Policy
                  </a>
                ) : (
                  <Link
                    to="/privacy-policy"
                    className="font-medium text-primary hover:underline"
                  >
                    Privacy Policy
                  </Link>
                )}
                .
              </p>
              <div className="mt-3 grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_2rem] gap-2">
                <Button
                  type="button"
                  size="sm"
                  className="h-8 w-full px-3 text-xs"
                  onClick={handleAcceptAll}
                  data-testid="cookie-accept-all"
                >
                  Accept All
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 w-full px-3 text-xs"
                  onClick={handleNecessaryOnly}
                  data-testid="cookie-necessary-only"
                >
                  Necessary Only
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 shrink-0 bg-transparent p-0 text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                  onClick={handleOpenModal}
                  aria-label="Manage cookie preferences"
                  title="Manage cookie preferences"
                  data-testid="cookie-manage-preferences"
                >
                  <Settings2 className="h-4 w-4" aria-hidden="true" />
                </Button>
              </div>
            </div>
          </div>
        </aside>
      )}

      <Dialog open={preferencesOpen} onOpenChange={setPreferencesOpen}>
        <DialogContent
          className="admin-modal-content"
          data-testid="cookie-preferences-dialog"
        >
          <DialogHeader className="admin-modal-header">
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary" aria-hidden="true" />
              Cookie &amp; Storage Preferences
            </DialogTitle>
            <DialogDescription>
              Control how Dibs uses cookies and browser storage. Strictly
              necessary storage is always active so authentication and security
              work properly.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-2">
            {/* Category 1: Strictly Necessary */}
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-foreground">
                    Strictly Necessary
                  </span>
                  <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                    Always Active
                  </span>
                </div>
                <p className="text-xs leading-relaxed text-muted-foreground">
                  Required for account sign-in, active business and outlet
                  sessions, OAuth security checks, and saving your cookie
                  consent choice.
                </p>
              </div>
              <Switch
                checked={true}
                disabled
                aria-label="Strictly Necessary cookies (always active)"
              />
            </div>

            {/* Category 2: Functional Preferences */}
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <label
                  htmlFor="cookie-switch-functional"
                  className="text-sm font-medium text-foreground cursor-pointer"
                >
                  Functional Preferences
                </label>
                <p className="text-xs leading-relaxed text-muted-foreground">
                  Remembers your UI layout preferences, such as whether the
                  dashboard sidebar is expanded or collapsed (`sidebar:state`).
                </p>
              </div>
              <Switch
                id="cookie-switch-functional"
                checked={functionalEnabled}
                onCheckedChange={setFunctionalEnabled}
                aria-label="Functional Preferences cookies"
                data-testid="cookie-switch-functional"
              />
            </div>

            {/* Category 3: Analytics & Diagnostics */}
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <label
                  htmlFor="cookie-switch-analytics"
                  className="text-sm font-medium text-foreground cursor-pointer"
                >
                  Analytics &amp; Diagnostics
                </label>
                <p className="text-xs leading-relaxed text-muted-foreground">
                  Helps us detect application crashes and performance bottlenecks
                  via anonymised Sentry error diagnostics (with personal data
                  stripped).
                </p>
              </div>
              <Switch
                id="cookie-switch-analytics"
                checked={analyticsEnabled}
                onCheckedChange={setAnalyticsEnabled}
                aria-label="Analytics and Diagnostics cookies"
                data-testid="cookie-switch-analytics"
              />
            </div>
          </div>

          <DialogFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between sm:space-x-0">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleNecessaryOnly}
              data-testid="cookie-modal-necessary-only"
            >
              Necessary Only
            </Button>
            <div className="flex flex-col-reverse gap-2 sm:flex-row">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={handleSaveCustomPreferences}
                data-testid="cookie-modal-save"
              >
                Save Preferences
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={handleAcceptAll}
                data-testid="cookie-modal-accept-all"
              >
                Accept All
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CookieConsentBanner;
